import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Ban, RefreshCw } from "lucide-react";
import {
  DeactivateUser,
  ListUserReports,
  UpdateReportStatus,
} from "../../services/backend";
import { userDisplayName } from "../../util/adminUsers";

const STATUS_COLORS = {
  open: "orange",
  reviewed: "blue",
  closed: "gray",
};

const REPEAT_THRESHOLD = 2;

export default function AdminReports({ users = [], onUserBanned }) {
  const toast = useToast();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [banningId, setBanningId] = useState(null);
  const [banTarget, setBanTarget] = useState(null);
  const cancelRef = useRef();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const muted = useColorModeValue("gray.600", "whiteAlpha.600");
  const border = useColorModeValue("gray.200", "whiteAlpha.100");
  const tableBg = useColorModeValue("white", "rgba(0,0,0,0.25)");

  const usersById = useMemo(() => {
    const map = new Map();
    for (const user of users) {
      if (user?.id) map.set(String(user.id), user);
    }
    return map;
  }, [users]);

  const labelFor = useCallback(
    (id) => {
      if (!id) return "—";
      const user = usersById.get(String(id));
      if (!user) return id;
      const name = userDisplayName(user);
      return name === id ? id : `${name}`;
    },
    [usersById]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await ListUserReports({ limit: 150 });
      setReports(rows);
    } catch (err) {
      toast({
        title: "Failed to load reports",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await UpdateReportStatus(id, status);
      setReports((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (err) {
      toast({
        title: "Could not update report",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const repeats = useMemo(() => {
    const map = new Map();
    for (const row of reports) {
      const id = row.reported_id;
      if (!id) continue;
      const entry = map.get(id) || {
        reportedId: id,
        count: 0,
        reporters: new Set(),
        openCount: 0,
        lastAt: row.created_at,
      };
      entry.count += 1;
      if (row.reporter_id) entry.reporters.add(row.reporter_id);
      if (row.status === "open") entry.openCount += 1;
      if (row.created_at && (!entry.lastAt || row.created_at > entry.lastAt)) {
        entry.lastAt = row.created_at;
      }
      map.set(id, entry);
    }
    return [...map.values()]
      .sort((a, b) => b.count - a.count || String(b.lastAt).localeCompare(String(a.lastAt)))
      .filter((entry) => entry.count >= REPEAT_THRESHOLD);
  }, [reports]);

  const confirmBan = (reportedId) => {
    const user = usersById.get(String(reportedId));
    setBanTarget({
      id: reportedId,
      name: labelFor(reportedId),
      alreadyBanned: user?.isActive === false,
    });
    onOpen();
  };

  const handleBan = async () => {
    if (!banTarget?.id) return;
    setBanningId(banTarget.id);
    try {
      await DeactivateUser(banTarget.id);
      const related = reports.filter((r) => r.reported_id === banTarget.id && r.status !== "closed");
      await Promise.all(
        related.map((r) => UpdateReportStatus(r.id, "closed").catch(() => null))
      );
      setReports((prev) =>
        prev.map((r) =>
          r.reported_id === banTarget.id ? { ...r, status: "closed" } : r
        )
      );
      toast({
        title: "User banned",
        description: `${banTarget.name} is deactivated and hidden from Discover.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onUserBanned?.();
      onClose();
      setBanTarget(null);
    } catch (err) {
      toast({
        title: "Ban failed",
        description: err?.response?.data?.message || err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setBanningId(null);
    }
  };

  const BanButton = ({ userId, size = "xs" }) => {
    const user = usersById.get(String(userId));
    const banned = user?.isActive === false;
    return (
      <Button
        size={size}
        colorScheme="red"
        variant={banned ? "outline" : "solid"}
        leftIcon={<Ban size={12} />}
        isDisabled={banned}
        isLoading={banningId === userId}
        onClick={() => confirmBan(userId)}
        borderRadius="full"
      >
        {banned ? "Banned" : "Ban"}
      </Button>
    );
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4} gap={3} flexWrap="wrap">
        <Text fontSize="sm" color={muted}>
          Ban hides the account from Discover and signs them out of the app. Use this for members with repeated reports.
        </Text>
        <Button
          size="sm"
          leftIcon={<RefreshCw size={14} />}
          onClick={load}
          isLoading={loading}
          borderRadius="full"
        >
          Refresh
        </Button>
      </Flex>

      <Text fontSize="sm" fontWeight="semibold" mb={2}>
        Repeatedly reported ({REPEAT_THRESHOLD}+ reports)
      </Text>
      <Box
        border="1px solid"
        borderColor={border}
        borderRadius="xl"
        overflowX="auto"
        bg={tableBg}
        mb={6}
      >
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Member</Th>
              <Th>Reports</Th>
              <Th>Reporters</Th>
              <Th>Open</Th>
              <Th>Last report</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {repeats.length === 0 ? (
              <Tr>
                <Td colSpan={6}>
                  <Text fontSize="sm" color={muted} py={4} textAlign="center">
                    {loading ? "Loading…" : "No members with multiple reports yet."}
                  </Text>
                </Td>
              </Tr>
            ) : (
              repeats.map((row) => (
                <Tr key={row.reportedId}>
                  <Td>
                    <Text fontWeight="600">{labelFor(row.reportedId)}</Text>
                    <Text fontSize="xs" color={muted} wordBreak="break-all">
                      {row.reportedId}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme="red">{row.count}</Badge>
                  </Td>
                  <Td>{row.reporters.size}</Td>
                  <Td>{row.openCount}</Td>
                  <Td whiteSpace="nowrap">
                    {row.lastAt ? new Date(row.lastAt).toLocaleString() : "—"}
                  </Td>
                  <Td>
                    <BanButton userId={row.reportedId} />
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      <Text fontSize="sm" fontWeight="semibold" mb={2}>
        All reports
      </Text>
      <Box
        border="1px solid"
        borderColor={border}
        borderRadius="xl"
        overflowX="auto"
        bg={tableBg}
      >
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>When</Th>
              <Th>Reporter</Th>
              <Th>Reported</Th>
              <Th>Reason</Th>
              <Th>Details</Th>
              <Th>Status</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {reports.length === 0 ? (
              <Tr>
                <Td colSpan={7}>
                  <Text fontSize="sm" color={muted} py={4} textAlign="center">
                    {loading ? "Loading…" : "No reports yet."}
                  </Text>
                </Td>
              </Tr>
            ) : (
              reports.map((row) => (
                <Tr key={row.id}>
                  <Td whiteSpace="nowrap">
                    {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
                  </Td>
                  <Td>
                    <Text fontSize="xs">{labelFor(row.reporter_id)}</Text>
                  </Td>
                  <Td>
                    <Text fontSize="xs" fontWeight="600">
                      {labelFor(row.reported_id)}
                    </Text>
                  </Td>
                  <Td>
                    <Badge>{row.reason}</Badge>
                  </Td>
                  <Td maxW="220px">
                    <Text fontSize="xs" noOfLines={2}>
                      {row.details || "—"}
                    </Text>
                  </Td>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Badge colorScheme={STATUS_COLORS[row.status] || "gray"}>
                        {row.status}
                      </Badge>
                      <Select
                        size="xs"
                        maxW="110px"
                        value={row.status}
                        onChange={(e) => setStatus(row.id, e.target.value)}
                      >
                        <option value="open">open</option>
                        <option value="reviewed">reviewed</option>
                        <option value="closed">closed</option>
                      </Select>
                    </Flex>
                  </Td>
                  <Td>
                    <BanButton userId={row.reported_id} />
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Ban this member?
            </AlertDialogHeader>
            <AlertDialogBody>
              {banTarget?.alreadyBanned
                ? `${banTarget?.name} is already banned.`
                : `${banTarget?.name} will be deactivated, hidden from Discover, and their open reports will be closed. They can be reactivated later from the user editor.`}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleBan}
                ml={3}
                isLoading={Boolean(banningId)}
                isDisabled={banTarget?.alreadyBanned}
              >
                Ban user
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
